"use client";
import React, {
  useState,
  Component,
  ErrorInfo,
  ReactNode,
  Suspense,
} from "react";
import { Page, Section } from "@/types/models/page";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import {
  selectSectionsArray,
  selectVisibleSections,
  selectPreviewHiddenSectionIds,
} from "@/store/selectors/webbuilder.selectors";
import {
  deleteSection,
  toggleSectionVisibility,
  toggleSectionLock,
  reorderSections,
} from "@/store/slices/webbuilderSlice";
import {
  Trash2,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  X,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
} from "lucide-react";
import { getComponentClientMap } from "@team-shiphouse/shiphouse-themes/client";
import "@team-shiphouse/shiphouse-themes/styles.css";
import { MockRouterProvider } from "../../providers/MockNextRouter";
import { ComponentMapEntry } from "@team-shiphouse/shiphouse-themes";
import WebBuilderProvider from "../../providers/WebBuilderProvider";
// Error Boundary for catching render errors in sections
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  sectionName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class SectionErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      `Section "${this.props.sectionName}" render error:`,
      error,
      errorInfo,
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Error rendering section</span>
            </div>
            <p className="text-sm text-red-600 mt-1">
              {this.props.sectionName
                ? `"${this.props.sectionName}"`
                : "This section"}{" "}
              failed to render.
            </p>
            {this.state.error && (
              <pre className="mt-2 text-xs text-red-500 overflow-auto max-h-20">
                {this.state.error.message}
              </pre>
            )}
          </div>
        )
      );
    }

    return this.props.children;
  }
}

// Mock SectionData type
export type SectionData = {
  [componentIdentifier: string]: {
    desktop: Array<{
      inputKey: string;
      type: string;
      value: unknown;
    }>;
    mobile: Array<{
      inputKey: string;
      type: string;
      value: unknown;
    }>;
    tablet: Array<{
      inputKey: string;
      type: string;
      value: unknown;
    }>;
  };
};

// Memoized Section Renderer Component
interface SectionRendererProps {
  section: Section;
  storeId: string;
  viewportMode: "desktop" | "tablet" | "mobile";
  showEditorControls: boolean;
  entry: ComponentMapEntry;
  isSelected: boolean;
  onSectionSelect?: (section: Section | null) => void;
  sortedSections: Section[];
  handleMoveSectionUp: (sectionId: string, e: React.MouseEvent) => void;
  handleMoveSectionDown: (sectionId: string, e: React.MouseEvent) => void;
  handleToggleLock: (sectionId: string, e: React.MouseEvent) => void;
  handleToggleVisibility: (sectionId: string, e: React.MouseEvent) => void;
  handleDeleteSection: (sectionId: string, e: React.MouseEvent) => void;
}

const SectionRenderer = React.memo(
  function SectionRenderer({
    section,
    storeId,
    viewportMode,
    showEditorControls,
    entry,
    isSelected,
    onSectionSelect,
    sortedSections,
    handleMoveSectionUp,
    handleMoveSectionDown,
    handleToggleLock,
    handleToggleVisibility,
    handleDeleteSection,
  }: SectionRendererProps) {
    console.log(`🔧 SectionRenderer render [${section.id}]:`, {
      viewportMode,
      sectionName: section.name,
    });
    const handleClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onSectionSelect) {
          onSectionSelect(section);
        }
      },
      [onSectionSelect, section],
    );

    const sectionIndex = React.useMemo(
      () => sortedSections.findIndex((s) => s.id === section.id),
      [sortedSections, section.id],
    );

    const isFirst = sectionIndex === 0;
    const isLast = sectionIndex === sortedSections.length - 1;
    const Component = entry.component as React.ComponentType<any>;

    return (
      <div
        className={`canvas-section relative group transition-all duration-200 cursor-pointer ${
          showEditorControls && isSelected
            ? "ring-2 ring-blue-500 ring-offset-2"
            : ""
        }`}
        onClick={showEditorControls ? handleClick : undefined}
      >
        {/* Section action buttons and highlight border */}
        {showEditorControls && isSelected && (
          <div className="absolute top-4 right-4 z-20 flex items-center space-x-2">
            {/* Move Up button */}
            <button
              onClick={(e) => handleMoveSectionUp(section.id, e)}
              disabled={isFirst}
              className="flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-border hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 group/btn disabled:opacity-50 disabled:cursor-not-allowed"
              title="Move section up"
            >
              <ChevronUp className="w-4 h-4 text-gray-600 group-hover/btn:text-gray-700" />
            </button>

            {/* Move Down button */}
            <button
              onClick={(e) => handleMoveSectionDown(section.id, e)}
              disabled={isLast}
              className="flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-border hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 group/btn disabled:opacity-50 disabled:cursor-not-allowed"
              title="Move section down"
            >
              <ChevronDown className="w-4 h-4 text-gray-600 group-hover/btn:text-gray-700" />
            </button>

            {/* Lock/Unlock button */}
            <button
              onClick={(e) => handleToggleLock(section.id, e)}
              className="flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-border hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 group/btn"
              title={section.is_locked ? "Unlock section" : "Lock section"}
            >
              {section.is_locked ? (
                <Unlock className="w-4 h-4 text-primary group-hover/btn:text-primary/80" />
              ) : (
                <Lock className="w-4 h-4 text-gray-600 group-hover/btn:text-gray-700" />
              )}
            </button>

            {/* Hide/Show button */}
            <button
              onClick={(e) => handleToggleVisibility(section.id, e)}
              className="flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-border hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 group/btn"
              title={section.is_hidden ? "Show section" : "Hide section"}
            >
              {section.is_hidden ? (
                <Eye className="w-4 h-4 text-amber-600 group-hover/btn:text-amber-700" />
              ) : (
                <EyeOff className="w-4 h-4 text-gray-600 group-hover/btn:text-gray-700" />
              )}
            </button>

            {/* Delete button */}
            <button
              onClick={(e) => handleDeleteSection(section.id, e)}
              className="flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-border hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 group/btn"
              title="Delete section"
            >
              <Trash2 className="w-4 h-4 text-red-600 group-hover/btn:text-red-700" />
            </button>
          </div>
        )}
        {/* Lock indicator - only show when locked and not selected */}
        {showEditorControls && section.is_locked && !isSelected && (
          <div className="absolute top-4 left-4 z-10 bg-gray-500 text-white text-xs px-2 py-1 rounded-full shadow-md">
            Locked
          </div>
        )}
        {/* Render sections */}
        <div className="pointer-events-none select-none">
          <SectionErrorBoundary
            sectionName={section.name || section.section_identifier}
          >
            <Suspense
              fallback={
                <div className="animate-pulse bg-gray-100 min-h-[200px] flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
                    <span className="text-gray-400 text-sm">
                      Loading section...
                    </span>
                  </div>
                </div>
              }
            >
              <Component
                key={`${section.id}-${viewportMode}-${JSON.stringify(section.component_data || {})}`}
                data={section.component_data}
                uniqueSectionIdentifier={section.id}
                storeId={storeId}
                viewportMode={viewportMode}
              />
            </Suspense>
          </SectionErrorBoundary>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    // Only re-render if these specific props change
    return (
      prevProps.section.id === nextProps.section.id &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.section.is_locked === nextProps.section.is_locked &&
      prevProps.section.is_hidden === nextProps.section.is_hidden &&
      prevProps.section.position === nextProps.section.position &&
      prevProps.showEditorControls === nextProps.showEditorControls &&
      prevProps.viewportMode === nextProps.viewportMode &&
      JSON.stringify(prevProps.section.component_data) ===
        JSON.stringify(nextProps.section.component_data) &&
      prevProps.sortedSections.length === nextProps.sortedSections.length
    );
  },
);

interface CanvasContentProps {
  currentPage: Page | null;
  storeId: string;
  viewportMode: "desktop" | "tablet" | "mobile";
  showEditorControls?: boolean;
  selectedSectionId?: string | null;
  onSectionSelect?: (section: Section | null) => void;
  dropIndicatorIndex?: number | null;
}

export const CanvasContent = React.memo(function CanvasContent({
  currentPage,
  storeId,
  viewportMode,
  showEditorControls = true,
  selectedSectionId,
  onSectionSelect,
  dropIndicatorIndex,
}: CanvasContentProps) {
  console.log("🎨 CanvasContent render:", {
    viewportMode,
    pageId: currentPage?.id,
  });
  const dispatch = useAppDispatch();
  const [hiddenNotification, setHiddenNotification] = useState<{
    sectionId: string;
    sectionName: string;
  } | null>(null);

  // Memoize the global component map (now returns ComponentMapEntry objects)
  const allComponentsMemo = React.useMemo(() => {
    const componentMap = getComponentClientMap();
    console.log("🗺️ Component map loaded:", {
      totalComponents: Object.keys(componentMap).length,
      componentIdentifiers: Object.keys(componentMap),
    });
    return componentMap;
  }, []);

  // Get the latest sections from Redux store to ensure real-time updates
  // Note: keep this selector minimal; per-section changes should be handled
  // by lighter-weight per-section containers in the next iteration.
  const sectionsFromStore = useAppSelector(selectSectionsArray) as Section[];

  console.log("🟢 CanvasContent: sectionsFromStore updated:", {
    length: sectionsFromStore.length,
    sectionIds: sectionsFromStore.map((s) => s.id),
  });

  // Use sections from store if available, otherwise fall back to currentPage sections
  const sectionsToRender = React.useMemo(() => {
    const sections =
      sectionsFromStore.length > 0
        ? sectionsFromStore
        : currentPage?.sections || [];
    console.log("🟢 CanvasContent: sectionsToRender memo recalculated:", {
      length: sections.length,
      sectionIds: sections.map((s: any) => s.id),
      source: sectionsFromStore.length > 0 ? "Redux" : "currentPage",
    });
    return sections;
  }, [sectionsFromStore, currentPage?.sections]);

  // Keep a memoized list of visible sections from the selector when possible
  const visibleSectionsFromStore = useAppSelector(
    selectVisibleSections,
  ) as Section[];
  const previewHiddenSectionIds = useAppSelector(
    selectPreviewHiddenSectionIds,
  ) as string[];

  console.log("🟢 CanvasContent: visibleSectionsFromStore updated:", {
    length: visibleSectionsFromStore.length,
    sectionIds: visibleSectionsFromStore.map((s) => s.id),
  });

  const visibleSections = React.useMemo(() => {
    const sections =
      sectionsFromStore.length > 0
        ? visibleSectionsFromStore
        : (currentPage?.sections || []).filter((s) => s && !s.is_hidden);
    // Filter out sections hidden from preview
    const filtered = sections.filter(
      (s) => !previewHiddenSectionIds.includes(s.id),
    );
    console.log("🟢 CanvasContent: visibleSections memo recalculated:", {
      length: filtered.length,
      sectionIds: filtered.map((s: any) => s.id),
      source: sectionsFromStore.length > 0 ? "Redux" : "currentPage",
      previewHiddenCount: previewHiddenSectionIds.length,
    });
    return filtered;
  }, [
    sectionsFromStore,
    visibleSectionsFromStore,
    currentPage?.sections,
    previewHiddenSectionIds,
  ]);

  // alias used by existing handlers
  const sortedSections: Section[] = sectionsToRender as Section[];

  console.log("🟢 CanvasContent render:", {
    visibleSectionsLength: visibleSections.length,
    sortedSectionsLength: sortedSections.length,
    storeId,
    currentPageId: currentPage?.id,
  });
  console.log("🗺️ Component map in CanvasContent:", visibleSections);

  // Handle section management actions
  const handleDeleteSection = React.useCallback(
    (sectionId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (
        confirm(
          "Are you sure you want to delete this section? This action cannot be undone.",
        )
      ) {
        dispatch(deleteSection(sectionId));
        if (onSectionSelect) {
          onSectionSelect(null);
        }
      }
    },
    [dispatch, onSectionSelect],
  );

  const handleToggleVisibility = React.useCallback(
    (sectionId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const section = sortedSections.find((s: any) => s.id === sectionId);
      if (section) {
        dispatch(toggleSectionVisibility(sectionId));

        // Show notification when hiding a section
        if (!section.is_hidden) {
          setHiddenNotification({
            sectionId: section.id,
            sectionName:
              section.name || section.section_identifier || "Section",
          });

          // Auto-hide notification after 5 seconds
          setTimeout(() => {
            setHiddenNotification(null);
          }, 5000);
        }
      }
    },
    [dispatch, sortedSections],
  );

  const handleToggleLock = React.useCallback(
    (sectionId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(toggleSectionLock(sectionId));
    },
    [dispatch],
  );

  const handleMoveSectionUp = React.useCallback(
    (sectionId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      console.log("Moving section up:", sectionId);
      const currentIndex = sortedSections.findIndex(
        (s: any) => s.id === sectionId,
      );
      console.log(
        "Current index:",
        currentIndex,
        "Total sections:",
        sortedSections.length,
      );
      if (currentIndex > 0) {
        // To move up, we need to swap with the previous section
        // We move the previous section down by making it the target
        const previousSection = sortedSections[currentIndex - 1];
        console.log(
          "Moving previous section down:",
          previousSection.id,
          "after:",
          sectionId,
        );
        dispatch(
          reorderSections({
            draggedId: previousSection.id,
            targetId: sectionId,
          }),
        );
      }
    },
    [dispatch, sortedSections],
  );

  const handleMoveSectionDown = React.useCallback(
    (sectionId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      console.log("Moving section down:", sectionId);
      const currentIndex = sortedSections.findIndex(
        (s: any) => s.id === sectionId,
      );
      console.log(
        "Current index:",
        currentIndex,
        "Total sections:",
        sortedSections.length,
      );
      if (currentIndex < sortedSections.length - 1) {
        // To move down, we move current section after the next section
        const nextSection = sortedSections[currentIndex + 1];
        console.log(
          "Moving current section:",
          sectionId,
          "after:",
          nextSection.id,
        );
        dispatch(
          reorderSections({ draggedId: sectionId, targetId: nextSection.id }),
        );
      }
    },
    [dispatch, sortedSections],
  );
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <span className="text-muted-foreground text-sm">
              Loading canvas...
            </span>
          </div>
        </div>
      }
    >
      {/* Hidden Section Notification */}
      {hiddenNotification && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-30 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg shadow-lg p-4 max-w-md">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <EyeOff className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Section Hidden
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                &ldquo;{hiddenNotification.sectionName}&rdquo; has been hidden.
                You can re-enable it from the Layers panel.
              </p>
            </div>
            <button
              onClick={() => setHiddenNotification(null)}
              className="flex-shrink-0 text-amber-400 hover:text-amber-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {visibleSections.length > 0 ? (
        <WebBuilderProvider sdkConfig={{ storeId, testMode: true }}>
          <MockRouterProvider pathname="/preview">
            {visibleSections.filter(Boolean).map((section, index) => {
              if (!section || !section.section_identifier) {
                console.warn("⚠️ Section missing identifier:", section);
                return null;
              }
              const entry = allComponentsMemo[section.section_identifier];
              console.log(
                `Rendering section ${section.id} with identifier ${section.section_identifier}:`,
                {
                  entry: entry,
                  sectionId: section.id,
                  sectionName: section.name,
                  sectionIdentifier: section.section_identifier,
                },
              );
              if (!entry) {
                console.error("❌ Missing component in map:", {
                  sectionId: section.id,
                  sectionName: section.name,
                  sectionIdentifier: section.section_identifier,
                  availableComponents: Object.keys(allComponentsMemo),
                });
                return null;
              }

              const isSelected = selectedSectionId === section.id;
              const showDropIndicatorBefore = dropIndicatorIndex === index;
              const showDropIndicatorAfter =
                dropIndicatorIndex === visibleSections.length &&
                index === visibleSections.length - 1;

              return (
                <React.Fragment key={section.id}>
                  {/* Drop indicator before section */}
                  {showDropIndicatorBefore && (
                    <div className="relative h-2 my-2 group">
                      <div className="absolute inset-x-0 h-0.5 bg-blue-500 animate-pulse"></div>
                      <div className="absolute inset-x-0 -top-1 h-2 bg-blue-500/20 rounded"></div>
                    </div>
                  )}

                  <SectionRenderer
                    section={section}
                    storeId={storeId}
                    viewportMode={viewportMode}
                    showEditorControls={showEditorControls}
                    entry={entry}
                    isSelected={isSelected}
                    onSectionSelect={onSectionSelect}
                    sortedSections={sortedSections}
                    handleMoveSectionUp={handleMoveSectionUp}
                    handleMoveSectionDown={handleMoveSectionDown}
                    handleToggleLock={handleToggleLock}
                    handleToggleVisibility={handleToggleVisibility}
                    handleDeleteSection={handleDeleteSection}
                  />

                  {/* Drop indicator after last section */}
                  {showDropIndicatorAfter && (
                    <div className="relative h-2 my-2 group">
                      <div className="absolute inset-x-0 h-0.5 bg-blue-500 animate-pulse"></div>
                      <div className="absolute inset-x-0 -top-1 h-2 bg-blue-500/20 rounded"></div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {/* Drop indicator for empty canvas */}
            {visibleSections.length === 0 && dropIndicatorIndex === 0 && (
              <div className="relative h-12 my-4 mx-4">
                <div className="absolute inset-x-0 top-1/2 h-1 bg-blue-500 animate-pulse -translate-y-1/2"></div>
                <div className="absolute inset-x-0 top-1/2 h-6 bg-blue-500/10 rounded -translate-y-1/2"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-500 text-white text-xs px-3 py-1 rounded-full whitespace-nowrap">
                  Drop section here
                </div>
              </div>
            )}
          </MockRouterProvider>
        </WebBuilderProvider>
      ) : (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <div className="w-16 h-16 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            No sections yet
          </h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Start building your page by adding sections from the sidebar. Choose
            from various pre-built components to create your perfect layout.
          </p>
        </div>
      )}
    </Suspense>
  );
});
